/**
 * Centralized education modal state management
 *
 * All education modals use a single localStorage key with a JSON object
 * to track which modals the user has seen.
 */

const EDUCATION_MODALS_KEY = 'squareup_education_modals_seen';

export type EducationModalId =
  | 'onboarding'
  | 'how_to_play'
  | 'theme_store'
  | 'accounting'
  | 'balances'
  | 'offline_players'
  | 'board_templates'
  | 'create_board'
  | 'referrals'
  | 'connections';

interface EducationModalsState {
  [key: string]: boolean;
}

/**
 * Get the current state of all education modals
 */
const getState = (): EducationModalsState => {
  try {
    const stored = localStorage.getItem(EDUCATION_MODALS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // If parsing fails, return empty state
  }
  return {};
};

/**
 * Save state to localStorage
 */
const saveState = (state: EducationModalsState): void => {
  try {
    localStorage.setItem(EDUCATION_MODALS_KEY, JSON.stringify(state));
  } catch {
    // Silently fail if localStorage is unavailable
  }
};

/**
 * Check if user has seen a specific education modal
 */
export const hasSeenEducationModal = (modalId: EducationModalId): boolean => {
  const state = getState();
  return state[modalId] === true;
};

/**
 * Mark an education modal as seen
 */
export const markEducationModalSeen = (modalId: EducationModalId): void => {
  const state = getState();
  state[modalId] = true;
  saveState(state);
};

/**
 * Reset a specific education modal (for testing)
 */
export const resetEducationModal = (modalId: EducationModalId): void => {
  const state = getState();
  delete state[modalId];
  saveState(state);
};

/**
 * Reset all education modals (for testing)
 */
export const resetAllEducationModals = (): void => {
  localStorage.removeItem(EDUCATION_MODALS_KEY);
};

/**
 * Get list of all seen modals (for debugging)
 */
export const getSeenModals = (): EducationModalId[] => {
  const state = getState();
  return Object.keys(state).filter(key => state[key]) as EducationModalId[];
};
